import {
  createContext,
  type FC,
  type ReactNode,
  useContext,
  useEffect,
} from "react";
import { useLocalStorage } from "usehooks-ts";

export enum DebugMode {
  None = "none",
  Basic = "basic",
  Verbose = "verbose",
}

export const DevToolsContext = createContext<
  | {
      devToolsEnabled: boolean;
      toggleDevTools: () => void;
      debugMode: DebugMode;
      setDebugMode: (mode: DebugMode) => void;
    }
  | undefined
>(undefined);

export const DevToolsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [devToolsEnabled, setDevToolsEnabled] = useLocalStorage<boolean>(
    "dev-tools-enabled",
    false,
  );

  const [debugMode, setDebugMode] = useLocalStorage<DebugMode>(
    "debug-mode",
    DebugMode.None,
  );

  // Reset debug mode to None when dev tools are disabled
  useEffect(() => {
    if (!devToolsEnabled && debugMode !== DebugMode.None) {
      setDebugMode(DebugMode.None);
    }
  }, [devToolsEnabled, debugMode, setDebugMode]);

  const toggleDevTools = () => setDevToolsEnabled((v) => !v);

  return (
    <DevToolsContext.Provider
      value={{ devToolsEnabled, toggleDevTools, debugMode, setDebugMode }}
    >
      {children}
    </DevToolsContext.Provider>
  );
};

export const useDevTools = () => {
  const context = useContext(DevToolsContext);
  if (!context) {
    return {
      devToolsEnabled: false,
      toggleDevTools: () => {},
      debugMode: DebugMode.None,
      setDebugMode: () => {},
    };
  }
  return context;
};
